import "./FormRequestManager.css"

import { Alert, message, Popconfirm, Empty } from "antd"
import { PaperClipOutlined } from "@ant-design/icons"

import { useGetReadyForApprovalRequestsMutation, useGetDownloadLinksMutation, useApproveMutation, rejectFormRequest, useRejectMutation } from "./formApiSlice"
import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import { showSpinner, hideSpinner } from "../../components/componentSlice"

const FormRequestManager = () => {

    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [messageApi, contextHolder] = message.useMessage()

    const [getReadyForApprovalRequests, {isLoading: isReadyForApprovalLoading}] = useGetReadyForApprovalRequestsMutation()
    const [getDownloadableLinks, {isLoading: isGetDownloadLinksLoading}] = useGetDownloadLinksMutation()
    const [approve, {isLoading: isApprovalLoading}] = useApproveMutation()
    const [reject, {isLoading: isRejectLoading}] = useRejectMutation()

    const [errorMessage, setErrorMessage] = useState(null)
    const [approvalRequests, setApprovalRequests] = useState([])

    useEffect(() => {
        if(isReadyForApprovalLoading || isGetDownloadLinksLoading || isApprovalLoading || isRejectLoading) {
            dispatch(showSpinner())
        } else{
            dispatch(hideSpinner())
        }
    }, [isReadyForApprovalLoading, isGetDownloadLinksLoading, isApprovalLoading, isRejectLoading])

    const showSuccessfulAlert = (message) => {
        messageApi.success(message)
    }

    useEffect(() => {
        async function handleGetApprovalRequests() {
            try {
                const response = await getReadyForApprovalRequests().unwrap()
                if(response) {
                    let requests = response.map(request => {
                        return {
                            advisor: request.advisor,
                            email: request.email,
                            id: request.id,
                            daysTillExpire: getDaysLeftFromTimestamp(request.lastUpdatedDate),
                            downloadLinks: []
                        }
                    })

                    setApprovalRequests(requests)
                }
            } catch(err) {
                if(err.status == 403) {
                    navigate("/dash/error403")
                } else {
                    setErrorMessage(err.data?.detail)
                }
            }
        }
       
        handleGetApprovalRequests()
    }, [])

    const handleDownloadableLinks = async (id) => {
        try {
            const response = await getDownloadableLinks(id).unwrap()
            if(response) {
                let updatedApprovalRequests = approvalRequests.map(request => {
                    if (request.id !== id) {
                        return request
                    }

                    let updated = {...request, 
                        downloadLinks: response
                    }

                    return updated
                })

                setApprovalRequests(updatedApprovalRequests)
            }
        } catch(err) {
            setErrorMessage(err.data?.detail)
        }
    }

    const approveFormRequest = async (id) => {
        try {
            await approve(id).unwrap()

            let updatedApprovalRequest = approvalRequests.filter(request => request.id !== id)
            setApprovalRequests(updatedApprovalRequest)

            showSuccessfulAlert("Request Approved!")
        } catch(err) {
            setErrorMessage(err.data?.detail)
        }
    }

    const rejectFormRequest = async (id) => {
        try {
            await reject(id).unwrap()

            let updatedApprovalRequest = approvalRequests.filter(request => request.id !== id)
            setApprovalRequests(updatedApprovalRequest)

            showSuccessfulAlert("Request Rejected!")
        } catch(err) {
            setErrorMessage(err.data?.detail)
        }
    }

    const createRequestCards = () => {
        return approvalRequests.map(request => {
            return (
                <div className="forms-request-card">
                    <div className="forms-request-header">
                        <div className="forms-request-id">
                            <p>Form Request Id: {request.id}</p>
                        </div>
                        {
                            request.downloadLinks?.length === 0 && 
                                <div className="forms-review-button">
                                    <button onClick={() => handleDownloadableLinks(request.id)}>Review</button>
                                </div>
                        }
                    </div>
                    <div className="forms-request-body">
                        <div>
                            <p><strong>Requested by:</strong> {request.advisor}</p>
                        </div>
                    </div>

                    <Alert message={`This request will be automatically rejected in ${request.daysTillExpire} day(s).`} description=" " banner />

                    {request.downloadLinks?.length > 0 &&
                        createDownloadableLinksForm(request.id, request.downloadLinks)
                    }
                </div>   
            )
        })
    }

    const getDaysLeftFromTimestamp = (timestampMs) => {
        const daysToExpire = 5

        const now = new Date()
        const targetDate = new Date(timestampMs + daysToExpire * 24 * 60 * 60 * 1000)
        const diffMs = targetDate - now

        let daysLeft = diffMs / (1000 * 60 * 60 * 24)
        return Math.floor(daysLeft)
    }

    const createDownloadableLinksForm = (id, downloadLinks) => {
        return (
            <div className="forms-review">
                <div className="review-instruction"><p>Please download the forms to review:</p></div>
                <div className="downloadable-links">
                    {
                        downloadLinks.sort().map((download) => {
                            return (
                                <a href={download.link} target='_blank'>
                                    <button className="download-link">
                                        <PaperClipOutlined /><p>{`${download.fileName}.zip`}</p>
                                    </button>
                                </a>
                            )
                        })
                    }
                </div>
                <div className="forms-approve-reject">
                    <Popconfirm
                        title="Reject"
                        description={`Are you sure to reject this request?`}
                        onConfirm={() => rejectFormRequest(id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <button className="reject-button">Reject</button>
                    </Popconfirm>
                    <button className="approve-button" onClick={() => approveFormRequest(id)}>Approve</button>
                </div>
            </div>
        )
    }

    return (
        <div className="table-page-container">
            {contextHolder}
            {errorMessage && 
                <div className="support-error">
                    <p>{errorMessage}</p>
                </div>
            }
            <div className="manage-forms-header">
                <div className='top-header'>
                    <header>Forms Request</header>
                </div>
            </div>

            {approvalRequests?.length > 0 ? 
                <div className="forms-request-container">
                    {createRequestCards()}
                </div>
                :
                <div className="no-file">
                    <Empty description={"No approval requests yet."} />
                </div>
            }
        </div>
    )
}

export default FormRequestManager